import * as React from "react"
import { cn } from "../../lib/utils"

/**
 * @author Matheus
 * Componente Skeleton para loading states elegantes
 * Mantém as cores originais da aplicação
 */
interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'text' | 'circular' | 'rectangular'
  width?: string | number
  height?: string | number
  lines?: number
}

const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  ({ className, variant = 'default', width, height, lines = 1, ...props }, ref) => {
    const baseClasses = "skeleton-refined animate-pulse bg-muted rounded"
    
    const variantClasses = {
      default: "h-4 w-full",
      text: "h-4 w-full",
      circular: "rounded-full",
      rectangular: "rounded-md"
    }

    const style: React.CSSProperties = {
      width: width || undefined,
      height: height || undefined,
    }

    if (variant === 'text' && lines > 1) {
      return (
        <div className="space-y-2" ref={ref} {...props}>
          {Array.from({ length: lines }).map((_, index) => (
            <div
              key={index}
              className={cn(
                baseClasses,
                variantClasses[variant],
                index === lines - 1 && "w-3/4", // Última linha mais curta
                className
              )}
              style={style}
            />
          ))}
        </div>
      )
    }

    return (
      <div
        ref={ref}
        className={cn(
          baseClasses,
          variantClasses[variant],
          className
        )}
        style={style}
        {...props}
      />
    )
  }
)
Skeleton.displayName = "Skeleton"

/**
 * Skeleton para Cards
 */
const CardSkeleton: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cn("card-hover gpu-accelerated rounded-lg border-border bg-card p-6 space-y-4", className)}>
    <div className="flex items-center space-x-4">
      <Skeleton variant="circular" width={40} height={40} />
      <div className="space-y-2 flex-1">
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="40%" />
      </div>
    </div>
    <Skeleton variant="text" lines={3} />
    <div className="flex justify-between items-center">
      <Skeleton variant="rectangular" width={80} height={32} />
      <Skeleton variant="rectangular" width={100} height={32} />
    </div>
  </div>
)

/**
 * Skeleton para Tabelas
 */
const TableSkeleton: React.FC<{ rows?: number; columns?: number; className?: string }> = ({ 
  rows = 5, 
  columns = 4, 
  className 
}) => (
  <div className={cn("space-y-3", className)}>
    {/* Header */}
    <div className="flex space-x-4">
      {Array.from({ length: columns }).map((_, index) => (
        <Skeleton key={`header-${index}`} variant="text" width="100%" className="h-6" />
      ))}
    </div>
    {/* Rows */}
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={`row-${rowIndex}`} className="flex space-x-4">
        {Array.from({ length: columns }).map((_, colIndex) => (
          <Skeleton key={`cell-${rowIndex}-${colIndex}`} variant="text" width="100%" />
        ))}
      </div>
    ))}
  </div>
)

/**
 * Skeleton para Dashboard Stats
 */
const StatsSkeleton: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cn("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6", className)}>
    {Array.from({ length: 4 }).map((_, index) => (
      <CardSkeleton key={`stat-${index}`} />
    ))}
  </div>
)

/**
 * Skeleton para Lista de Leads
 */
const LeadListSkeleton: React.FC<{ items?: number; className?: string }> = ({ 
  items = 6, 
  className 
}) => (
  <div className={cn("space-y-4", className)}>
    {Array.from({ length: items }).map((_, index) => (
      <div key={`lead-${index}`} className="card-hover gpu-accelerated rounded-lg border-border bg-card p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-1">
            <Skeleton variant="circular" width={32} height={32} />
            <div className="space-y-2 flex-1">
              <Skeleton variant="text" width="70%" />
              <Skeleton variant="text" width="50%" />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Skeleton variant="rectangular" width={60} height={24} />
            <Skeleton variant="rectangular" width={80} height={32} />
          </div>
        </div>
      </div>
    ))}
  </div>
)

/**
 * Skeleton para Gráficos
 */
const ChartSkeleton: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cn("card-hover gpu-accelerated rounded-lg border-border bg-card p-6", className)}>
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Skeleton variant="text" width="40%" className="h-6" />
        <Skeleton variant="rectangular" width={100} height={32} />
      </div>
      <div className="h-64 flex items-end justify-between space-x-2">
        {Array.from({ length: 7 }).map((_, index) => (
          <Skeleton 
            key={`bar-${index}`} 
            variant="rectangular" 
            width="100%" 
            height={Math.random() * 200 + 50} 
          />
        ))}
      </div>
    </div>
  </div>
)

export {
  Skeleton,
  CardSkeleton,
  TableSkeleton,
  StatsSkeleton,
  LeadListSkeleton,
  ChartSkeleton
}